import type { GatewayReturnPayload } from '../types';

/**
 * The single definition of this app's one gateway-return route. Before
 * this, the same URL shape was spelled out in four places — once per
 * adapter's buildReturnUrl/buildCancelUrl, plus again in PaymentPage's
 * own buildCallbackPath, which carried a comment apologising for the
 * duplication. A gateway id or param name changing in one of them and not
 * the others is exactly the drift this closes: every caller now derives
 * the URL from here.
 */
export const CALLBACK_ROUTE = '/payment/callback';

export const CALLBACK_PARAMS = {
  gateway: 'gateway',
  orderId: 'order_id',
  status: 'status',
  paymentId: 'payment_id',
} as const;

export interface CallbackTarget {
  gatewayId: string;
  orderId: string;
  /** Omitted on a plain return URL; 'cancelled' is what a cancel URL carries. */
  status?: GatewayReturnPayload['claimedStatus'];
  paymentId?: string | null;
}

function buildSearch({
  gatewayId,
  orderId,
  status,
  paymentId,
}: CallbackTarget): URLSearchParams {
  // `gateway` first so the query reads the same way it always has —
  // `/payment/callback?gateway=<id>&order_id=<id>`.
  const params = new URLSearchParams({
    [CALLBACK_PARAMS.gateway]: gatewayId,
    [CALLBACK_PARAMS.orderId]: orderId,
  });

  if (status) params.set(CALLBACK_PARAMS.status, status);
  if (paymentId) params.set(CALLBACK_PARAMS.paymentId, paymentId);

  return params;
}

/**
 * Relative — for react-router's navigate(), the same-origin in-app hop an
 * inline-SDK gateway makes after its checkout settles.
 */
export function buildCallbackPath(target: CallbackTarget): string {
  return `${CALLBACK_ROUTE}?${buildSearch(target)}`;
}

/**
 * Absolute — this is handed to an external gateway as a redirect target,
 * so it has to carry the origin.
 */
export function buildCallbackUrl(target: CallbackTarget): string {
  return `${window.location.origin}${buildCallbackPath(target)}`;
}

/**
 * The return/cancel pair every adapter exposes. They differ only in the
 * `status=cancelled` marker, which is what lets the callback page tell a
 * deliberate cancellation apart from a return it has to go verify.
 */
export function buildGatewayUrls(gatewayId: string, orderId: string) {
  return {
    returnUrl: buildCallbackUrl({ gatewayId, orderId }),
    cancelUrl: buildCallbackUrl({ gatewayId, orderId, status: 'cancelled' }),
  };
}
